import type { KeyboardEvent } from "react";
import { useEffect, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";

import { useBrandAutocomplete } from "app/pages/digital-marketing/generative-ai/ai-research/shared/hooks/useBrandAutocomplete";

import type {
    IComboboxOptionItem,
    IUseBrandAutocompleteComboboxParams,
    IUseBrandAutocompleteComboboxResult,
} from "./types";
import { useAnchoredPortal } from "./useAnchoredPortal";

const USE_TYPED_OPTION_ID = "__use_typed__";

export const useBrandAutocompleteCombobox = ({
    query,
    onSelect,
    excludedBrands = [],
    disabled = false,
    showTypedAsOption,
}: IUseBrandAutocompleteComboboxParams): IUseBrandAutocompleteComboboxResult => {
    const { translate } = useTranslate();
    const { suggestions, isLoading, isError } = useBrandAutocomplete(query, excludedBrands);
    const [isDismissed, setIsDismissed] = useState<boolean>(false);
    const [highlightedIndex, setHighlightedIndex] = useState<number>(0);
    const trimmedQuery = query.trim();
    const hasQuery = trimmedQuery.length > 0;
    const suggestionKey = suggestions.map((suggestion) => suggestion.name).join("|");
    const canOpen = !disabled && hasQuery && !isDismissed;
    const isOpen = showTypedAsOption ? canOpen : canOpen && !isLoading && !isError;
    const { position, anchorRef, portalRef } = useAnchoredPortal(isOpen, () => setIsDismissed(true));
    const options: IComboboxOptionItem[] = [
        ...(showTypedAsOption && hasQuery
            ? [
                  {
                      id: USE_TYPED_OPTION_ID,
                      label: translate("llm.brandAutocomplete.use_typed", { query: trimmedQuery }),
                      brand: trimmedQuery,
                  },
              ]
            : []),
        ...suggestions.map((suggestion) => ({ id: suggestion.name, label: suggestion.name, brand: suggestion.name })),
    ];

    const activeIndex = options.length === 0 ? -1 : Math.min(highlightedIndex, options.length - 1);
    const emptyState = isOpen && !showTypedAsOption && options.length === 0 ? translate("dropdown.no.results") : null;

    useEffect(() => {
        setIsDismissed(false);
    }, [query]);

    useEffect(() => {
        setHighlightedIndex(0);
    }, [suggestionKey]);

    const handleSelect = (brand: string): void => {
        onSelect(brand);
        setIsDismissed(true);
    };

    const handleKeyDownCapture = (event: KeyboardEvent<HTMLDivElement>): void => {
        if (!isOpen || options.length === 0) {
            return;
        }

        const consumeEvent = (): void => {
            event.preventDefault();
            event.stopPropagation();
        };

        switch (event.key) {
            case "ArrowDown":
                consumeEvent();
                setHighlightedIndex((current) => (current + 1) % options.length);
                return;
            case "ArrowUp":
                consumeEvent();
                setHighlightedIndex((current) => (current - 1 + options.length) % options.length);
                return;
            case "Enter": {
                const highlightedBrand = options[activeIndex]?.brand;
                if (!highlightedBrand) {
                    return;
                }
                consumeEvent();
                handleSelect(highlightedBrand);
                return;
            }
            case "Escape":
                consumeEvent();
                setIsDismissed(true);
                return;
            default:
                return;
        }
    };

    return {
        isOpen,
        isLoading,
        options,
        emptyState,
        activeIndex,
        position,
        anchorRef,
        portalRef,
        handleSelect,
        handleKeyDownCapture,
    };
};
